// src/components/question1/Question1.js
import React, { useEffect, useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../context/AppContext';
import { v4 as uuidv4 } from 'uuid';
import { TIMEOUT } from '../../config/index.js';
import Navbar from '../navbar/Navbar.js';
import AdBanner from '../ad_banner/AdBanner.js';
import businessLogo from '../../images/business_logo.png';
import { ToastContainer } from 'react-toastify';
import { showToast } from '../Notify.js';
import VirtualSuccessUI from '../virtual_ui/VirtualSuccessUI.js';
import { insertQuestion1, scheduleRemoveSyncedEntries, syncDirectToRDS } from '../../db/indexedDb.js';
import './question1.css';
import { SYNC_FREQUENCY, ANIMATION_FREQUENCY, CLEAR_LOCAL_DB_FREQUENCY, FACES_RANDOM_ANIMATION_FREQUENCY } from '../../config';

// Import enhanced sync functions
import { enhancedSyncDataToCloud, getUnsyncedCount } from '../../db/enhancedIndexedDBSync';
import { statusDetector } from '../../utils/statusDetector';
import { api } from '../../api/index';

// import external endpoint functions
import { sendExternalFeedback, getLocalDateTime } from '../../db/externalEndpoints/externalFeedbackService.js';

// Import default fallback images (static only)
const face1 = require('../../images/face1.png');
const face2 = require('../../images/face2.png');
const face3 = require('../../images/face3.png');
const face4 = require('../../images/face4.png');
const face5 = require('../../images/face5.png');

// Import GIFs for animation
const face1_gif = require('../../images/face1.gif');
const face2_gif = require('../../images/face2.gif');
const face3_gif = require('../../images/face3.gif');
const face4_gif = require('../../images/face4.gif');
const face5_gif = require('../../images/face5.gif');

const Question1 = () => {
  const [gifNumber, setGifNumber] = useState('0');
  const [autoGifIndex, setAutoGifIndex] = useState(null);
  const [googleReviewUrlStatus, setGoogleReviewUrlStatus] = useState('loading');
  const [showVirtualSuccess, setShowVirtualSuccess] = useState(false);
  
  // Dynamic icons state
  const [dynamicIcons, setDynamicIcons] = useState([]);
  const [iconsLoading, setIconsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ Get ad banners AND complaint options from global context
  const { 
    setResponse, 
    deck, 
    adBanners, 
    adsEnabled,
    complaintOptions,
    complaintSlideEnabled 
  } = useContext(AppContext);
  
  const timerRef = useRef(null);
  const navigate = useNavigate();

  console.log(`adsEnabled: ${adsEnabled}`);

  // get business_id and deck_id from the first entry in the deck (slides array) - this is used for conditional logic and external endpoint payloads
  const business_id = deck.length > 0 ? deck[0].business_id : null;
  const deck_id = deck.length > 0 ? deck[0].deck_id : null;

  // Filter slide with type = "rating"
  console.log('Filtering deck for rating slide. Deck:', deck);
  const filteredDeck = deck.length > 0 ? deck.filter((slide) => slide.type === 'rating') : [];
  const filteredSlide = filteredDeck.length > 0 ? filteredDeck[0] : null;
 
  let device_name = filteredSlide?.device_name;
  const title = filteredSlide?.title ?? "How did we do today?";
  const background_color = filteredSlide?.background_color ?? '#fafafa';
  const icon_set_id = filteredSlide?.icon_set_id ?? 'default_faces';
  const show_icon_labels = filteredSlide?.show_icon_labels || false;
  const icon_labels = filteredSlide?.icon_labels ? (typeof filteredSlide.icon_labels === 'string' ? JSON.parse(filteredSlide.icon_labels) : filteredSlide.icon_labels) : {};

  const text_style = filteredSlide?.text_style ?? {
    font_size: '50px',
    font_weight: 'normal',
    font_family: 'Arial, sans-serif',
    font_style: 'normal',
    color: '#000000',
  };

  const resetTime = TIMEOUT;
  const animationTimeout = ANIMATION_FREQUENCY;

  // Default fallback icons array
  const defaultIcons = [
    { sentiment_value: 5, icon_url: face1, animated_icon_url: face1_gif, alt_text: 'Very Happy' },
    { sentiment_value: 4, icon_url: face2, animated_icon_url: face2_gif, alt_text: 'Happy' },
    { sentiment_value: 3, icon_url: face3, animated_icon_url: face3_gif, alt_text: 'Neutral' },
    { sentiment_value: 2, icon_url: face4, animated_icon_url: face4_gif, alt_text: 'Sad' },
    { sentiment_value: 1, icon_url: face5, animated_icon_url: face5_gif, alt_text: 'Very Sad' }
  ];

  // PERSISTENT CACHE FUNCTIONS using localStorage (ICONS ONLY)
  const ICON_CACHE_KEY = 'icon_sets_cache';
  const ICON_CACHE_TIMESTAMP_KEY = 'icon_sets_cache_timestamp';
  const CACHE_EXPIRY_DAYS = 7; // Cache expires after 7 days

  const getCachedIcons = (iconSetId) => {
    try {
      const cacheStr = localStorage.getItem(ICON_CACHE_KEY);
      if (!cacheStr) return null;

      const cache = JSON.parse(cacheStr);
      const cachedSet = cache[iconSetId];
      
      if (!cachedSet) return null;

      // Check cache age
      const timestampStr = localStorage.getItem(ICON_CACHE_TIMESTAMP_KEY);
      if (timestampStr) {
        const timestamps = JSON.parse(timestampStr);
        const cacheTime = timestamps[iconSetId];
        
        if (cacheTime) {
          const cacheAge = Date.now() - cacheTime;
          const maxAge = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
          
          if (cacheAge > maxAge) {
            console.log(`Cache expired for ${iconSetId}, age: ${Math.round(cacheAge / 86400000)} days`);
            return null;
          }
        }
      }

      console.log(`✅ Using cached icons for: ${iconSetId}`);
      return cachedSet;
    } catch (error) {
      console.error('Error reading icon cache:', error);
      return null;
    }
  };

  const setCachedIcons = (iconSetId, icons) => {
    try {
      // Save icons
      const cacheStr = localStorage.getItem(ICON_CACHE_KEY);
      const cache = cacheStr ? JSON.parse(cacheStr) : {};
      cache[iconSetId] = icons;
      localStorage.setItem(ICON_CACHE_KEY, JSON.stringify(cache));

      // Save timestamp
      const timestampStr = localStorage.getItem(ICON_CACHE_TIMESTAMP_KEY);
      const timestamps = timestampStr ? JSON.parse(timestampStr) : {};
      timestamps[iconSetId] = Date.now();
      localStorage.setItem(ICON_CACHE_TIMESTAMP_KEY, JSON.stringify(timestamps));

      console.log(`💾 Cached icons for: ${iconSetId}`);
    } catch (error) {
      console.error('Error saving icon cache:', error);
    }
  };

  // Fetch icons for a specific icon set with persistent caching
  const fetchIconsForSet = async (iconSetId) => {
    // 1. Check if default
    if (!iconSetId || iconSetId === 'default_faces') {
      console.log('Using default faces for icon set:', iconSetId);
      return defaultIcons;
    }

    // 2. Try to get from cache first (works offline)
    const cachedIcons = getCachedIcons(iconSetId);
    if (cachedIcons) {
      // If offline or backend down, use cache immediately
      if (!navigator.onLine) {
        console.log('📡 Device offline - using cached icons');
        return cachedIcons;
      }
      
      // If online, use cache but try to update in background
      console.log('Using cached icons, will update in background');
      // Don't await - let it update in background
      fetchAndUpdateIconCache(iconSetId).catch(err => {
        console.warn('Background icon update failed:', err);
      });
      return cachedIcons;
    }

    // 3. No cache available - try to fetch from backend
    if (!navigator.onLine) {
      console.warn('❌ Device offline and no cache available - using default icons');
      return defaultIcons;
    }

    try {
      console.log(`📥 Fetching dynamic icons for set: ${iconSetId}`);
      const icons = await fetchAndUpdateIconCache(iconSetId);
      return icons;
    } catch (error) {
      console.error('❌ Error fetching icons, using defaults:', error);
      return defaultIcons;
    }
  };

  // Separate function to fetch and update cache
  const fetchAndUpdateIconCache = async (iconSetId) => {
    try {
      const response = await fetch(`${api}/icon-sets/${iconSetId}/icons`, {
        signal: AbortSignal.timeout(5000) // 5 second timeout
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch icons: ${response.status}`);
      }
      
      const iconsData = await response.json();
      const icons = iconsData?.icons;
      
      if (!icons || icons.length !== 5) {
        console.warn(`Icon set ${iconSetId} has ${icons?.length || 0} icons, expected 5`);
        throw new Error('Invalid icon count');
      }

      console.log(`✅ Fetched ${icons.length} icons for ${iconSetId}`);
      
      // Update cache
      setCachedIcons(iconSetId, icons);
      
      return icons;
    } catch (error) {
      // Check if we're offline or backend is down
      if (error.name === 'AbortError' || error.message?.includes('Failed to fetch')) {
        console.warn('⚠️ Network error fetching icons:', error.message);
      }
      throw error;
    }
  };

  // Load icons when icon_set_id changes
  useEffect(() => {
    const loadIcons = async () => {
      if (!filteredSlide) {
        console.log('No filtered slide found, using default icons');
        setDynamicIcons(defaultIcons);
        return;
      }

      console.log('🎨 Loading icons for icon set:', icon_set_id);
      
      setIconsLoading(true);
      
      try {
        const icons = await fetchIconsForSet(icon_set_id);
        setDynamicIcons(icons);
        console.log('✅ Dynamic icons loaded successfully');
      } catch (error) {
        console.error('❌ Failed to load dynamic icons, using defaults:', error);
        setDynamicIcons(defaultIcons);
      } finally {
        setIconsLoading(false);
      }
    };

    loadIcons();
  }, [icon_set_id]);

  // Get icon by sentiment value
  const getIconBySentiment = (sentimentValue, animated = false) => {
    const icon = dynamicIcons.find(icon => icon.sentiment_value === parseInt(sentimentValue));
    if (icon) {
      // If animated version is requested and available, use it; otherwise use static
      if (animated && icon.animated_icon_url) {
        return icon.animated_icon_url;
      }
      return icon.icon_url;
    }
    
    // Fallback to default icon
    const defaultIcon = defaultIcons.find(icon => icon.sentiment_value === parseInt(sentimentValue));
    if (defaultIcon) {
      return animated && defaultIcon.animated_icon_url ? defaultIcon.animated_icon_url : defaultIcon.icon_url;
    }
    return face3;
  };

  // Render icon button with dynamic icon support and animation
  const renderIconButton = (sentimentValue, className = '') => {
    const shouldAnimate = gifNumber === sentimentValue.toString() || autoGifIndex === sentimentValue;
    const iconSrc = getIconBySentiment(sentimentValue, shouldAnimate);
    const icon = dynamicIcons.find(icon => icon.sentiment_value === sentimentValue);
    const altText = icon ? icon.alt_text : `Face ${sentimentValue}`;
    const labelText = icon_labels[sentimentValue] || altText;

    return (
      <button
        disabled={isSubmitting || (gifNumber !== '0' && gifNumber !== sentimentValue.toString())}
        className={`question1-button ${className}`}
        onClick={() => handleSubmitQuestion(sentimentValue.toString())}
      >
        <img
          className={`question1-image ${className.includes('mobile') ? 'mobile-image' : ''}`}
          src={iconSrc}
          alt={altText}
          onError={(e) => {
            console.warn('Icon failed to load, using fallback:', iconSrc);
            const defaultIcon = defaultIcons.find(icon => icon.sentiment_value === sentimentValue);
            if (defaultIcon) {
              e.target.src = shouldAnimate && defaultIcon.animated_icon_url 
                ? defaultIcon.animated_icon_url 
                : defaultIcon.icon_url;
            }
          }}
        />
        {show_icon_labels && 
          <span 
            className="icon-label"
            style={{
              '--font-weight': text_style.font_weight,
              '--font-family': text_style.font_family,
              '--font-style': text_style.font_style,
              '--color': text_style.color 
            }}
          >
            {labelText}
          </span>
        }
      </button>
    );
  };

  const getCurrentDateTimeUTC = () => {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    const hours = String(now.getUTCHours()).padStart(2, '0');
    const minutes = String(now.getUTCMinutes()).padStart(2, '0');
    const seconds = String(now.getUTCSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Improved Google Review URL validation
  const validateGoogleReviewUrl = async (retryCount = 0) => {
    const maxRetries = 2;
    
    try {
      const deviceId = localStorage.getItem("device_id");
      
      if (!deviceId || deviceId.trim() === "" || deviceId === "undefined" || deviceId === null) {
        console.warn("No valid device ID found for Google review validation");
        setGoogleReviewUrlStatus('unavailable');
        return false;
      }

      console.log(`Validating Google review URL for device: ${deviceId} (attempt ${retryCount + 1})`);
      
      const response = await fetch(`${api}/scan/${deviceId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!Array.isArray(data) || data.length === 0) {
        console.log("No Google review data found for this device");
        localStorage.removeItem("google_review_url");
        setGoogleReviewUrlStatus('unavailable');
        return false;
      }

      const googleReviewUrl = data[0]?.google_review_url;
      
      if (googleReviewUrl && googleReviewUrl.trim() !== "" && googleReviewUrl !== "null" && googleReviewUrl !== "undefined") {
        console.log("Valid Google review URL found:", googleReviewUrl);
        localStorage.setItem("google_review_url", googleReviewUrl);
        setGoogleReviewUrlStatus('available');
        return true;
      } else {
        console.log("Google review URL is empty or invalid");
        localStorage.removeItem("google_review_url");
        setGoogleReviewUrlStatus('unavailable');
        return false;
      }

    } catch (error) {
      console.error(`Error validating Google review URL (attempt ${retryCount + 1}):`, error);
      
      if (retryCount < maxRetries) {
        const delay = Math.pow(2, retryCount) * 1000;
        console.log(`Retrying Google review validation in ${delay}ms...`);
        
        setTimeout(() => {
          validateGoogleReviewUrl(retryCount + 1);
        }, delay);
        
        return false;
      }
      
      console.error("Max retries reached for Google review validation");
      setGoogleReviewUrlStatus('error');
      localStorage.removeItem("google_review_url");
      return false;
    }
  };

  // Check if Google Review is available
  const hasValidGoogleReviewUrl = () => {
    // first check if the deck has any slide with type 'google_review'
    const hasGoogleReviewSlide = deck.slides?.some(slide => slide.type === 'google_review');
    if (!hasGoogleReviewSlide) {
      // set local storage to indicate no google review slide
       localStorage.removeItem("google_review_url");
      return false;
    }
    
    const url = localStorage.getItem("google_review_url");
    return googleReviewUrlStatus === 'available' && 
           url && 
           url.trim() !== "" && 
           url !== "null" && 
           url !== "undefined";
  };

  // ✅ NEW: Check if Complaint slide is available and enabled
  const hasComplaintSlideEnabled = () => {
    console.log('Checking complaint slide status:', {
      complaintSlideEnabled,
      complaintOptionsCount: complaintOptions?.length || 0,
      business_id
    });

    // Adelaide Oval (business_id 503109) never shows complaints
    if (business_id === 503109) {
      console.log('Adelaide Oval - complaints disabled');
      return false;
    }

    // Check if complaint slide is enabled and has options
    const isEnabled = complaintSlideEnabled && 
                     complaintOptions && 
                     complaintOptions.length > 0;

    console.log('Complaint slide enabled:', isEnabled);
    return isEnabled;
  };

  // Initialize Google Review URL validation on component mount
  useEffect(() => {
    const initializeGoogleReviewValidation = async () => {
      await validateGoogleReviewUrl();
    };
    initializeGoogleReviewValidation();
  }, []);

  // poll every minute if store_id becomes "null" or ""
  useEffect(() => {
    const interval = setInterval(() => {
      const storedStoreId = localStorage.getItem('store_id');
      if (!storedStoreId || storedStoreId === "null" || storedStoreId.trim() === "") {
        console.log("store_id is invalid, navigating to home");
        navigate('/');
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [navigate]);

  // on first load, check if store_id is valid
  useEffect(() => {
    const storedStoreId = localStorage.getItem('store_id');
    if (!storedStoreId || storedStoreId === "null" || storedStoreId.trim() === "") {
      console.log("store_id is invalid on load, navigating to home");
      navigate('/');
    }
  }, [navigate]);

    const handleSubmitQuestion = async (answerQ1) => {
      if (isSubmitting) return;

      setIsSubmitting(true);

      // Generate a unique customer_feedback_id for this feedback session
      const feedbackSessionId = uuidv4();

      setResponse(prevState => ({
        ...prevState,
        customer_feedback_id: feedbackSessionId,
        question1: {
          ...prevState.question1,
          response_value: answerQ1,
          datetime_: getCurrentDateTimeUTC(),
        },
      }));

      setGifNumber(answerQ1);

      setTimeout(async () => {
        setGifNumber('0');

        const hasGoogleReview = hasValidGoogleReviewUrl();
        const hasComplaintEnabled = hasComplaintSlideEnabled(); // ✅ Check complaint status
        const device_type = localStorage.getItem('device_type');
        const device_id = localStorage.getItem('device_id');
        const device_name = localStorage.getItem('device_name');
        const deck_id = filteredSlide?.deck_id || localStorage.getItem('deck_id');
        const deck_name = filteredSlide?.deck_name || localStorage.getItem('deck_name');
        const deck_question = filteredSlide?.title || localStorage.getItem('deck_question');
        const datetime_ = getCurrentDateTimeUTC();

        // Prepare payload for Adelaide Oval external endpoint
        let payload_adelaide_oval = {};

        if (business_id === 503109) {
          payload_adelaide_oval = {
            id: crypto.randomUUID(),
            device_name: device_name,
            device_id: device_id,
            datetime: getLocalDateTime(),
            question_type: "score",
            deck_id: deck_id,
            deck_name: deck_name,
            deck_question: deck_question,
            outcome: { score: answerQ1 },
          };
        }

        // For virtual devices, sync directly to RDS immediately
        if (device_type === 'virtual') {

          // Send feedback to Adelaide Oval external endpoint
          if (business_id === 503109) {
            console.log(`Sending feedback to Adelaide Oval external endpoint as business_id is ${business_id}`);
              try {
              const org_id = 1; // Replace with actual org_id as needed
              const response = await sendExternalFeedback(payload_adelaide_oval, org_id);
              if (response.success) {
                console.log('Successfully sent feedback to Adelaide Oval external endpoint');
              } else {
                console.error('Failed to send feedback to Adelaide Oval external endpoint');
              }
            } catch (error) {
              console.error('Error sending feedback to Adelaide Oval external endpoint:', error);
            }
          }

          // Attempt direct sync to RDS
          try {
            const syncResult = await syncDirectToRDS({
              question1: [{
                device_id: device_id,
                response_value: answerQ1,
                datetime_: datetime_,
                customer_feedback_id: feedbackSessionId,
                synced: 1 // Mark as synced since we're sending directly
              }],
              question2: [],
              question3: [],
              complaint: []
            });

            if (!syncResult.success) {
              console.error('Failed to sync to RDS, falling back to IndexedDB');
              // Fallback to IndexedDB if direct sync fails
              await insertQuestion1(device_id, answerQ1, datetime_, payload_adelaide_oval, feedbackSessionId);
            }
          } catch (error) {
            console.error('Error in direct RDS sync:', error);
            // Fallback to IndexedDB
            await insertQuestion1(device_id, answerQ1, datetime_, payload_adelaide_oval, feedbackSessionId);
          }
        } else {
          // For physical devices, use IndexedDB as usual
          await insertQuestion1(device_id, answerQ1, datetime_, payload_adelaide_oval, feedbackSessionId);
        }

        // ✅ UPDATED: Navigation logic based on score
        if (answerQ1 === '5' || answerQ1 === '4') {
          // Happy responses (4 or 5)
          localStorage.setItem("response_type", "happy");
          if (hasGoogleReview) {
            navigate('/GoogleReview');
          } else {
            showSuccessMessage();
          }
        } else {
          // Sad/Neutral responses (1, 2, or 3)
          localStorage.setItem("response_type", "sad");
          
          // ✅ Only navigate to complaint if it's enabled
          if (hasComplaintEnabled) {
            console.log('Navigating to Complaint page');
            navigate('/Complaint');
          } else {
            console.log('Complaint slide disabled - showing success message');
            showSuccessMessage();
          }
        }
        
        setIsSubmitting(false);
      }, animationTimeout);

      resetTimer();
    };

  const showSuccessMessage = () => {
    const device_type = localStorage.getItem('device_type');
    if (device_type === 'virtual') {
      setShowVirtualSuccess(true);
    } else {
      showToast('Thank you for your feedback! We appreciate your response.', 'success');
      setTimeout(() => {
        navigate('/question1');
      }, 2500);
    }
  };

  // Sync data to cloud
  useEffect(() => {
    console.log('Starting enhanced sync scheduler');
    
    const syncInterval = setInterval(async () => {
      try {
        const result = await enhancedSyncDataToCloud();
        console.log('Sync result:', result);
      } catch (error) {
        console.error('Sync scheduler error:', error);
      }
    }, SYNC_FREQUENCY);

    return () => clearInterval(syncInterval);
  }, []);

  // Add periodic health checks
  useEffect(() => {
    console.log('Starting health check scheduler');
    
    const healthCheckInterval = setInterval(async () => {
      try {
        await statusDetector.performFullHealthCheck(getUnsyncedCount);
      } catch (error) {
        console.error('Health check error:', error);
      }
    }, 60000); // Every minute

    // Initial health check
    statusDetector.performFullHealthCheck(getUnsyncedCount).catch(error => {
      console.error('Initial health check failed:', error);
    });

    return () => clearInterval(healthCheckInterval);
  }, []);

  // Add network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      console.log('Device back online - triggering health check');
      statusDetector.performFullHealthCheck(getUnsyncedCount);
    };

    const handleOffline = () => {
      console.log('Device went offline');
      statusDetector.activeIssues.add('offline');
      statusDetector.updateStatus({
        networkChange: true,
        online: false
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Clear local db
  useEffect(() => {
    const intervalId = setInterval(scheduleRemoveSyncedEntries, CLEAR_LOCAL_DB_FREQUENCY);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    startTimer();
    return () => clearTimer();
  }, []);
  
  const startTimer = () => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      navigate('/question1');
    }, resetTime);
  };

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  };

  const resetTimer = () => {
    startTimer();
  };

  // Auto GIF animation
  useEffect(() => {
    const animateRandomFace = () => {
      const randomIndex = Math.floor(Math.random() * 5) + 1;
      setAutoGifIndex(randomIndex);

      setTimeout(() => {
        setAutoGifIndex(null);
      }, FACES_RANDOM_ANIMATION_FREQUENCY);
    };

    const intervalId = setInterval(animateRandomFace, 5000);
    return () => clearInterval(intervalId);
  }, []);

  if (iconsLoading) {
    return (
      <div className='question1-page-container' style={{ backgroundColor: background_color }}>
        <div className='question1-container'>
          <Navbar className="question1-navbar" />
          <div className='question1-text-container'>
            <div className='question1-text'>Loading feedback icons...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    showVirtualSuccess ? <VirtualSuccessUI /> :
    <div className='question1-page-container' style={{ backgroundColor: background_color }}>
      <div className='question1-container'>
        <Navbar className="question1-navbar" />
        <div className='question1-text-container'>
          <div 
            className='question1-text'
            style={{
              '--font-size': text_style.font_size,
              '--font-weight': text_style.font_weight,
              '--font-family': text_style.font_family,
              '--font-style': text_style.font_style,
              '--color': text_style.color 
            }}
          >
            {title}
          </div>
        </div>

        <div className='question1-facesContainer'>
          {/* Desktop Layout - Horizontal Row */}
          <div className='question1-sub-facesContainer desktop-layout'>
            {renderIconButton(5)}
            {renderIconButton(4)}
            {renderIconButton(3)}
            {renderIconButton(2)}
            {renderIconButton(1)}
          </div>

          {/* Mobile Layout - Corrected for proper thumb zone */}
          <div className='question1-mobile-layout mobile-layout'>
            {/* Top Row - Bad ratings (1,2) - hard to reach */}
            <div className='mobile-row mobile-top-row'>
              {renderIconButton(1, 'mobile-button')}
              {renderIconButton(2, 'mobile-button')}
            </div>

            {/* Middle Row - Neutral rating (3) */}
            <div className='mobile-row mobile-middle-row'>
              {renderIconButton(3, 'mobile-button')}
            </div>

            {/* Bottom Row - Good ratings (4,5) - thumb zone */}
            <div className='mobile-row mobile-bottom-row'>
              {renderIconButton(5, 'mobile-button')}
              {renderIconButton(4, 'mobile-button')}
            </div>
          </div>
        </div>
        
        <ToastContainer />
        
        {/* ✅ Dynamic Ad Banner - Using global state from AppContext */}
        {adsEnabled && (
          <AdBanner images={adBanners} />
        )}

        <div className="mobile-bottom-container">
          <img src={businessLogo} alt="Business Logo" className="business-logo-mobile" />
        </div>
      </div>
    </div>
  );
};

export default Question1;